import { TReceivedInvoiceCondition } from '@/context/services/invoice/type';
import { useEnhanceInvoiceListSearchFlag } from '@/hooks/feature_flag/useEnhanceInvoiceListSearchFlag';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useTranslation } from '@/i18n';
import { TableEmptyType } from '@moneyforward/ap-frontend-components';
import parser from 'html-react-parser';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const useReceivedInvoicesFormInitial = (): TReceivedInvoiceCondition => {
  const location = useLocation();
  const { getArrayParam, getDateParam } = useQueryParams();
  const flagInvoiceListDueDateAmount = useEnhanceInvoiceListSearchFlag();

  return useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = Number(searchParams.get('page')) || 1;

    return {
      ap_payee_id_in: getArrayParam(searchParams, 'ap_payee_id_in'),
      searchable_number_or_filename_cont:
        searchParams.get('searchable_number_or_filename_cont') || '',
      operation_status_in: getArrayParam(searchParams, 'operation_status_in'),
      pic_id_in: getArrayParam(searchParams, 'pic_id_in'),
      upload_type_in: getArrayParam(searchParams, 'upload_type_in'),
      uploaded_date: [
        getDateParam(searchParams, 'uploaded_at_gteq'),
        getDateParam(searchParams, 'uploaded_at_lteq'),
      ],
      // NA-5501 filters exist only while the flag is on. Ignoring these params
      // when it is off stops a URL saved during rollout from silently applying
      // a filter the user has no UI to see or clear.
      ...(flagInvoiceListDueDateAmount
        ? {
            due_date: [
              getDateParam(searchParams, 'due_date_from'),
              getDateParam(searchParams, 'due_date_to'),
            ],
            total_amount_from: searchParams.get('total_amount_from') || '',
            total_amount_to: searchParams.get('total_amount_to') || '',
          }
        : {}),
      page,
    };
  }, [
    location.search,
    getArrayParam,
    getDateParam,
    flagInvoiceListDueDateAmount,
  ]);
};

export const useEmptyState = (isSearchEmpty = false) => {
  const { t } = useTranslation();
  const isOnline = useNetworkStatus();
  const [emptyType, setEmptyType] = useState<TableEmptyType | undefined>(
    undefined
  );
  const [emptyMessage, setEmptyMessage] = useState<ReactNode | undefined>(
    undefined
  );
  useEffect(() => {
    if (!isOnline) {
      setEmptyType('error');
      setEmptyMessage(parser(t('system_error_network')));
    } else if (isSearchEmpty) {
      setEmptyType('search');
      setEmptyMessage(t('invoice_list_notfound_message'));
    } else {
      setEmptyType('file');
      setEmptyMessage(t('invoice_list_empty_message'));
    }
  }, [t, isOnline, isSearchEmpty]);

  return {
    emptyType,
    emptyMessage,
  };
};
